/**
 * Feature Flags List — master pane: search + type/state/owner filters + stale entry.
 */

import { useMemo, useState } from 'react';
import { Search, AlertTriangle } from 'lucide-react';
import { FlagRow } from './FlagRow';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useI18n } from '@/i18n';
import type { FlagResponse, FlagType, LifecycleState } from '@/types/featureFlags';
import { getStaleFlags } from '../utils/stale';

interface FlagListProps {
  flags: FlagResponse[];
  selectedFlagKey: string | null;
  onSelect: (flagKey: string) => void;
  onOpenRetirement: () => void;
}

const FLAG_TYPES: FlagType[] = ['deployment', 'release', 'isolation', 'ops'];
const STATES: LifecycleState[] = ['draft', 'active', 'archived'];

export function FlagList({ flags, selectedFlagKey, onSelect, onOpenRetirement }: FlagListProps) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');

  const owners = useMemo(
    () => Array.from(new Set(flags.map((f) => f.owner).filter((o): o is string => !!o))).sort(),
    [flags]
  );

  const staleCount = useMemo(() => getStaleFlags(flags).length, [flags]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return flags.filter((f) => {
      if (q && !f.flag_key.toLowerCase().includes(q) && !(f.display_name ?? '').toLowerCase().includes(q))
        return false;
      if (typeFilter && f.flag_type !== typeFilter) return false;
      if (stateFilter && f.lifecycle_state !== stateFilter) return false;
      if (ownerFilter && f.owner !== ownerFilter) return false;
      return true;
    });
  }, [flags, search, typeFilter, stateFilter, ownerFilter]);

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="p-4 space-y-3 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('featureFlags.flagList.searchPlaceholder')}
            className="pl-9"
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            options={[
              { value: '', label: t('featureFlags.flagList.allTypes') },
              ...FLAG_TYPES.map((ft) => ({ value: ft, label: ft })),
            ]}
          />
          <Select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            options={[
              { value: '', label: t('featureFlags.flagList.allStates') },
              ...STATES.map((s) => ({ value: s, label: t(`featureFlags.lifecycleState.${s}`) })),
            ]}
          />
          <Select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            options={[
              { value: '', label: t('featureFlags.flagList.allOwners') },
              ...owners.map((o) => ({ value: o, label: o })),
            ]}
          />
        </div>
        {staleCount > 0 && (
          <button
            type="button"
            onClick={onOpenRetirement}
            className="flex items-center gap-1.5 w-full px-3 py-2 text-sm rounded-lg bg-yellow-50 text-yellow-800 border border-yellow-200 hover:bg-yellow-100 transition-colors"
          >
            <AlertTriangle className="w-4 h-4" />
            {t('featureFlags.flagList.staleEntry', { count: String(staleCount) })}
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {filtered.length === 0 ? (
          <div className="text-center py-12 px-4 text-gray-500">
            <p className="text-sm">
              {flags.length === 0 ? t('featureFlags.flagList.noFlags') : t('featureFlags.flagList.noMatch')}
            </p>
            {flags.length === 0 && (
              <p className="text-xs mt-1">{t('featureFlags.flagList.noFlagsHint')}</p>
            )}
          </div>
        ) : (
          filtered.map((flag) => (
            <FlagRow
              key={flag.flag_key}
              flag={flag}
              selected={flag.flag_key === selectedFlagKey}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}
